import { createDebugger } from './debug.js';

const debug = createDebugger('global-video-pause', false);

/**
 * Global Video Pause Utility
 * Pauses video loading during cart operations and variant changes
 */
class GlobalVideoPause {
    constructor() {
        this.isOperationActive = false;
        this.pausedVideos = new Set();
        this.pausedVideoSources = new Set();

        // Listen for variant changes to pause videos
        this.initVariantChangeListener();

        debug.log('GlobalVideoPause initialized');
    }

    /**
     * Initialize variant change listener
     */
    initVariantChangeListener() {
        document.addEventListener('product:variant-change', () => {
            debug.log('Variant change detected - pausing videos');
            this.startOperation('variant-change');

            // Resume videos after a short delay to allow variant change to complete
            setTimeout(() => {
                this.endOperation('variant-change');
            }, 1000);
        });
    }

    /**
     * Start an operation (cart or variant change)
     */
    startOperation(type = 'unknown') {
        this.isOperationActive = true;
        debug.log(`Operation started: ${type}`);
        this.pauseVideoLoading();
    }

    /**
     * End an operation (cart or variant change)
     */
    endOperation(type = 'unknown') {
        this.isOperationActive = false;
        debug.log(`Operation completed: ${type}`);
        this.resumeVideoLoading();
    }

    /**
     * Legacy method for cart operations
     */
    startCartOperation() {
        this.startOperation('cart');
    }

    /**
     * Legacy method for cart operations
     */
    endCartOperation() {
        this.endOperation('cart');
    }

    /**
     * Pause video loading during operations
     */
    pauseVideoLoading() {
        if (!this.isOperationActive) return;

        debug.log('Pausing video loading during operation');

        // Clear any existing paused sets
        this.pausedVideos.clear();
        this.pausedVideoSources.clear();

        // 1. Pause videos with data-load attributes (responsive-video component)
        const lazyVideos = document.querySelectorAll(
            'video[data-load-load], video[data-load-dom], video[data-load-manual]'
        );
        lazyVideos.forEach((video) => {
            if (!video.src && !video.dataset.pausedByGlobalUtil) {
                video.dataset.pausedByGlobalUtil = 'true';
                this.pausedVideos.add(video);
                debug.log('Paused lazy video', { video });
            }
        });

        // 2. Pause video sources that haven't loaded yet
        const videoSources = document.querySelectorAll('video source[data-src]');
        videoSources.forEach((source) => {
            if (source.dataset.src && !source.src && !source.dataset.pausedByGlobalUtil) {
                source.dataset.pausedSrc = source.dataset.src;
                source.dataset.pausedByGlobalUtil = 'true';
                delete source.dataset.src;
                this.pausedVideoSources.add(source);
                debug.log('Paused video source', { source });
            }
        });

        // 3. Pause videos with preload attributes
        const preloadVideos = document.querySelectorAll('video[preload="metadata"], video[preload="auto"]');
        preloadVideos.forEach((video) => {
            if (!video.dataset.originalPreload && !video.dataset.pausedByGlobalUtil) {
                video.dataset.originalPreload = video.preload;
                video.dataset.pausedByGlobalUtil = 'true';
                video.preload = 'none';
                this.pausedVideos.add(video);
                debug.log('Paused preload video', { video });
            }
        });

        // 4. Pause any actively loading videos
        const loadingVideos = document.querySelectorAll('video:not([data-paused-by-global-util])');
        loadingVideos.forEach((video) => {
            if (
                video.readyState === HTMLMediaElement.HAVE_NOTHING ||
                video.readyState === HTMLMediaElement.HAVE_METADATA
            ) {
                if (video.networkState === HTMLMediaElement.NETWORK_LOADING) {
                    video.dataset.pausedByGlobalUtil = 'true';
                    video.pause();
                    this.pausedVideos.add(video);
                    debug.log('Paused actively loading video', { video });
                }
            }
        });

        debug.log('Video pausing complete', {
            pausedVideos: this.pausedVideos.size,
            pausedSources: this.pausedVideoSources.size
        });
    }

    /**
     * Resume video loading after operations
     */
    resumeVideoLoading() {
        debug.log('Resuming video loading after operation');

        // Resume paused videos
        this.pausedVideos.forEach((video) => {
            if (video.dataset.pausedByGlobalUtil) {
                delete video.dataset.pausedByGlobalUtil;

                // Restore original preload if it was changed
                if (video.dataset.originalPreload) {
                    video.preload = video.dataset.originalPreload;
                    delete video.dataset.originalPreload;
                }

                debug.log('Resumed video', { video });
            }
        });

        // Resume paused video sources
        this.pausedVideoSources.forEach((source) => {
            if (source.dataset.pausedSrc && source.dataset.pausedByGlobalUtil) {
                source.dataset.src = source.dataset.pausedSrc;
                delete source.dataset.pausedSrc;
                delete source.dataset.pausedByGlobalUtil;
                debug.log('Resumed video source', { source });
            }
        });

        // Clear the paused sets
        this.pausedVideos.clear();
        this.pausedVideoSources.clear();

        debug.log('Video resuming complete');
    }

    /**
     * Enable debug logging
     */
    enableDebug() {
        debug.enabled = true;
    }

    /**
     * Disable debug logging
     */
    disableDebug() {
        debug.enabled = false;
    }
}

// Create global instance
const globalVideoPause = new GlobalVideoPause();

export default globalVideoPause;
